import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Coin } from '@sorare/core/src/atoms/icons/Coin';
import { NewScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { ArenaTicket } from '@sorare/core/src/atoms/icons/rivals/branded/ArenaTicket';
import { Reputation } from '@sorare/core/src/atoms/icons/rivals/branded/Reputation';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { StrokedText } from '@sorare/core/src/atoms/typography/StrokedText';
import { isType } from '@sorare/core/src/gql';

import { BoxOddsDialog } from 'components/rewards/BoxOddsDialog';

import { RewardPreviewWrapper } from './RewardPreviewWrapper';
import {
  RewardPreview_managerTaskRewardInterface,
  RewardPreview_rewardConfig,
} from './__generated__/index.graphql';

const BoxPicture = styled.img`
  height: var(--triple-unit);
  object-fit: cover;
`;

const CardPackWrapper = styled.div`
  position: relative;
`;
const CountWrapper = styled.div`
  position: absolute;
  right: calc(-1 * var(--half-unit));
  bottom: calc(-1 * var(--half-unit));
  z-index: 1;
`;

const Chip = styled(Horizontal).attrs({ gap: 1, center: true })`
  width: var(--triple-unit);
  height: var(--triple-unit);
  gap: var(--unit);
  --color: 173, 173, 173;
  border-radius: var(--half-unit);
  border: 0.5px solid transparent;
  background:
    linear-gradient(#262626, #262626) padding-box,
    linear-gradient(rgb(var(--color)), rgba(var(--color), 0.2)) border-box;
  background-repeat: no-repeat;
  & > * {
    filter: drop-shadow(0 0 2px rgba(var(--color), 1));
  }
`;

type ImagePreviewProps = {
  probabilisticBundle: Nullable<
    RewardPreview_rewardConfig['probabilisticBundleConfiguration']
  >;
  arenaTickets?: Nullable<number>;
  coins?: Nullable<number>;
  reputation?: number;
  small?: boolean;
  cardPack?: number;
};
const ImagePreview = ({
  probabilisticBundle,
  arenaTickets,
  coins,
  reputation,
  small,
  cardPack,
}: ImagePreviewProps) => {
  const [boxOddsDialogOpen, setBoxOddsDialogOpen] = useState(false);
  if (probabilisticBundle) {
    return (
      <>
        <ButtonBase onClick={() => setBoxOddsDialogOpen(true)}>
          <BoxPicture
            src={probabilisticBundle.pictureUrl}
            alt={probabilisticBundle.name}
          />
        </ButtonBase>
        <BoxOddsDialog
          onClose={() => setBoxOddsDialogOpen(false)}
          open={boxOddsDialogOpen}
          probabilisticBundleConfig={probabilisticBundle}
        />
      </>
    );
  }
  if (reputation) {
    return <Reputation height={small ? 20 : 24} count={reputation} />;
  }
  if (arenaTickets) {
    return <ArenaTicket width={small ? 12 : 14} count={arenaTickets} />;
  }
  if (coins) {
    return <Coin />;
  }
  if (cardPack) {
    return (
      <CardPackWrapper>
        <Chip>
          <NewScarcityIcon rarity={Rarity.common} size={2} />
        </Chip>
        <CountWrapper>
          <StrokedText yOffset={1} strokeWidth={2}>
            {/* TODO: replace hardcoded 5 cards per packgi with API value */}
            <LabelM bold>{cardPack * 5}</LabelM>
          </StrokedText>
        </CountWrapper>
      </CardPackWrapper>
    );
  }
  return null;
};

type RewardDescriptionProps = {
  probabilisticBundle: Nullable<
    RewardPreview_rewardConfig['probabilisticBundleConfiguration']
  >;
  arenaTickets?: Nullable<number>;
  coins?: Nullable<number>;
  reputation?: number;
  cardPack?: number;
};
const RewardDescription = ({
  probabilisticBundle,
  arenaTickets,
  coins,
  reputation,
  cardPack,
}: RewardDescriptionProps) => {
  if (probabilisticBundle) {
    return probabilisticBundle.name;
  }
  if (reputation) {
    return (
      <FormattedMessage
        id="MissionsPage.DailyChallenge.repReward"
        defaultMessage="{nb, plural, =0 {{nb} Rep Point} one {{nb} Rep Point} other {{nb} Rep Points}}"
        values={{ nb: reputation }}
      />
    );
  }
  if (arenaTickets) {
    return (
      <FormattedMessage
        id="MissionsPage.DailyChallenge.arenaTicketReward"
        defaultMessage="{nb, plural, =0 {{nb} Arena Ticket} one {{nb} Arena Ticket} other {{nb} Arena Tickets}}"
        values={{ nb: arenaTickets }}
      />
    );
  }
  if (coins) {
    return (
      <FormattedMessage
        id="MissionReward.coin.tooltip"
        defaultMessage="Coin x{count}"
        values={{ count: coins }}
      />
    );
  }
  if (cardPack) {
    return (
      <FormattedMessage
        id="MissionReward.cardPack.tooltip"
        defaultMessage="Common Card Pack"
      />
    );
  }
  return null;
};

type Props = {
  reward: RewardPreview_managerTaskRewardInterface | RewardPreview_rewardConfig;
  showDescription?: boolean;
};
export const RewardPreview = ({ reward, showDescription }: Props) => {
  let probabilisticBundle;
  let arenaTickets;
  let coins;
  let reputation;
  let cardPack;

  if (isType(reward, 'RewardConfig')) {
    arenaTickets = reward.arenaTicketAmount;
    coins = reward.coinAmount;
    probabilisticBundle = reward.probabilisticBundleConfiguration;
  } else {
    if (isType(reward, 'ArenaTicketReward')) {
      arenaTickets = reward.count;
    }
    if (isType(reward, 'ReputationReward')) {
      reputation = reward.count;
    }
    if (isType(reward, 'ProbabilisticBundleConfigReward')) {
      probabilisticBundle = reward.probabilisticBundleConfig;
    }
    if (isType(reward, 'CardPackReward')) {
      cardPack = reward.count;
    }
  }

  if (showDescription) {
    return (
      <Horizontal>
        <RewardPreviewWrapper>
          <ImagePreview
            probabilisticBundle={probabilisticBundle}
            arenaTickets={arenaTickets}
            coins={coins}
            reputation={reputation}
            cardPack={cardPack}
            small
          />
        </RewardPreviewWrapper>
        <LabelM as="span">
          <RewardDescription
            probabilisticBundle={probabilisticBundle}
            arenaTickets={arenaTickets}
            coins={coins}
            reputation={reputation}
            cardPack={cardPack}
          />
        </LabelM>
      </Horizontal>
    );
  }

  return (
    <Tooltip
      title={
        <LabelM>
          <RewardDescription
            probabilisticBundle={probabilisticBundle}
            arenaTickets={arenaTickets}
            coins={coins}
            reputation={reputation}
            cardPack={cardPack}
          />
        </LabelM>
      }
    >
      <ImagePreview
        probabilisticBundle={probabilisticBundle}
        arenaTickets={arenaTickets}
        coins={coins}
        reputation={reputation}
        cardPack={cardPack}
      />
    </Tooltip>
  );
};

RewardPreview.fragments = {
  managerTaskRewardInterface: gql`
    fragment RewardPreview_managerTaskRewardInterface on ManagerTaskRewardInterface {
      ... on ArenaTicketReward {
        count
      }
      ... on ReputationReward {
        count
      }
      ... on CardPackReward {
        count
      }
      ... on ProbabilisticBundleConfigReward {
        probabilisticBundleConfig {
          id
          pictureUrl
          name
          ...BoxOddsDialog_probabilisticBundleConfig
        }
      }
    }
    ${BoxOddsDialog.fragments.probabilisticBundleConfig}
  ` as TypedDocumentNode<RewardPreview_managerTaskRewardInterface>,
  rewardConfig: gql`
    fragment RewardPreview_rewardConfig on RewardConfig {
      arenaTicketAmount
      coinAmount
      probabilisticBundleConfiguration {
        id
        pictureUrl
        name
        ...BoxOddsDialog_probabilisticBundleConfig
      }
    }
    ${BoxOddsDialog.fragments.probabilisticBundleConfig}
  ` as TypedDocumentNode<RewardPreview_rewardConfig>,
};
